import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  Col,
  Input,
  Row,
  Table,
} from "reactstrap";
import FalconCardHeader from "../common/FalconCardHeader";
import FullPagination from "../common/pagination/FullPagination";
import PropTypes from "prop-types";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import { fetchCondominiumBanks, remove } from "../../helpers/api/banks";
import { isIterableArray } from "../../helpers/utils";
import BankListItem from "../common/BankListItem";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { Toast } from "../UI/toast/Toast";
import withRedirect from "../../helpers/hoc/withRedirect";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import ButtonIcon from "../common/ButtonIcon";
import {
  faInfoCircle,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { Message, Or, PreloadState } from "../UI/preloading/PreloadState";
import classNames from "classnames";
import PeriodListItem from "../common/PeriodListItem";
import { deletePeriod, fetchCondominiumPeriod } from "../../helpers/api/finance";

const CondominiumPeriodList = ({
  setRedirectUrl,
  setRedirect,
  title,
  condominiumId,
  showPagination,
}) => {
  const { role } = useSelector((state) => state.auth.user);
  const [textSearch, setTextSearch] = useState("");
  const [params, setParams] = useState({
    condominiumId: condominiumId,
    search: null,
  });
  const {
    data,
    error,
    isLoading,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchCondominiumPeriod, params);

  const handleDelete = async (period) => {
    try {
      await deletePeriod(period.id);
      Toast.fire({ type: "success", title: `Período ${period.name} eliminado.` });
      setParams((rest) => ({ ...rest }));
    } catch (error) {
      handleErrorMessage(error);
    }
  };


  const renderPeriodItems = () => {
    return data.map((period) => {
      return (
        <PeriodListItem
          key={period?.id}
          id={period?.id}
          title={period?.name}
          start_at={period?.start_at}
          end_at={period?.end_at}
          createdAt={period?.created_at}
          discount_total={period?.discount_total}
          discount_percentage={period?.discount_percentage}
          discount_due_date={period?.discount_due_date}
          onDeleteAction={period?.can_delete ? () => handleDelete(period) : undefined}
          alternativeName="período"
          options={role === "manager"}
        />
      );
    });
  };

  return (
    <>
      <Card>
        <FalconCardHeader title={title}>
          <Row>
            <Col className="d-flex">
              <Input
                type="text"
                value={textSearch}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  const value = e.currentTarget.value.trim();
                  setTextSearch(value);
                  setParams((rest) => ({ ...rest, search: value || null }));
                }}
                onChange={(event) => {
                  const value = event.target.value;
                  setTextSearch(value);
                  if (value.trim() === "") {
                    setParams((rest) => ({ ...rest, search: null }));
                  }
                }}
                placeholder="Nombre o fecha (DD/MM/AAAA)..."
                className="d-inline-block search-input mr-2"
              />
              <ButtonIcon
                color="falcon-default"
                className="text-nowrap"
                icon={faSearch}
                onClick={() => {
                  const value = textSearch.trim();
                  setTextSearch(value);
                  setParams((rest) => ({ ...rest, search: value || null }));
                }}
              >
                Buscar
              </ButtonIcon>
            </Col>
            {role === "manager" && (
              <Col
                xs={12}
                md={"auto"}
                className="mt-3 mt-md-0 text-center text-md-right"
              >
                <ButtonIcon
                  icon={faPlus}
                  color="falcon-default"
                  onClick={() => {
                    setRedirect(true);
                    setRedirectUrl(
                      `/dashboard/periods/create/${condominiumId}`
                    );
                  }}
                >
                  Añadir período
                </ButtonIcon>
              </Col>
            )}
          </Row>
        </FalconCardHeader>
        <CardBody
          className={classNames("fs--1", { "p-0": isIterableArray(data) })}
        >
          <PreloadState cond={isIterableArray(data)} loading={isLoading}>
            <Or value={true}>
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th className="align-middle">Período</th>
                    <th className="text-center align-middle">Fecha de inicio</th>
                    <th className="text-center align-middle">Fecha fin</th>
                    <th className="text-center align-middle">
                      Fecha de creación
                    </th>
                    <th className="text-center align-middle">
                      Descuentos otorgados
                    </th>
                    {role === "manager" && (
                      <th className="text-right align-middle">Acciones</th>
                    )}
                  </tr>
                </thead>
                <tbody>{renderPeriodItems()}</tbody>
              </Table>
            </Or>
            <Or value={false} cond={!!error}>
              <Or value={false} cond={role}>
                <Or value={"manager"} cond={!!textSearch[0]}>
                  <Message
                    value={true}
                    color={"info"}
                    className="p-3 mr-3"
                    align="left"
                    icon={faInfoCircle}
                  >
                    <>
                      No hemos encontrado un período que coincida con{" "}
                      <b>"{textSearch}"</b>, puedes crearlo presionando el botón{" "}
                      <b>"Añadir período"</b>.
                    </>
                  </Message>
                  <Message
                    value={false}
                    color={"info"}
                    className="p-3 mr-3"
                    align="left"
                    icon={faInfoCircle}
                  >
                    No hay períodos registrados, puedes crear uno presionando el
                    botón "Añadir período".
                  </Message>
                </Or>
                <Or value={"property"} cond={!!textSearch[0]}>
                  <Message
                    value={true}
                    color={"info"}
                    className="p-3 mr-3"
                    align="left"
                    icon={faInfoCircle}
                  >
                    <>
                      No hemos encontrado un período que coincida con{" "}
                      <b>"{textSearch}"</b>.
                    </>
                  </Message>
                  <Message
                    value={false}
                    color={"info"}
                    className="p-3 mr-3"
                    align="left"
                    icon={faInfoCircle}
                  >
                    Aún no hay períodos en este condominio.
                  </Message>
                </Or>
              </Or>
              <Or value={true}>
                <Alert color="danger" className="mb-0 mr-3">
                  Esta página no está disponible
                  <Button
                    data-testid="Button"
                    color="link"
                    size="sm"
                    className="ml-1 pl-0"
                    onClick={() => {
                      setRedirectUrl(`/dashboard/periods`);
                      setRedirect(true);
                    }}
                  >
                    Volver
                  </Button>
                </Alert>
              </Or>
            </Or>
          </PreloadState>
        </CardBody>
        {isIterableArray(data) && (
          <CardFooter className="border-top mt-0">
            <FullPagination
              nextPage={nextPage}
              prevPage={previousPage}
              goToPage={goToPage}
              meta={meta}
              className={"shadow-none"}
              posText={"Periodos"}
              perPage={perPage}
            />
          </CardFooter>
        )}
      </Card>
    </>
  );
};

CondominiumPeriodList.propTypes = {
  showPagination: PropTypes.bool,
};
CondominiumPeriodList.defaultProps = {
  title: "Listado de períodos del condominio",
  description: "",
};

export default withRedirect(CondominiumPeriodList);
